/**
 * GlobalSearch — site-wide search UI.
 *
 * Layout:
 *   • Header shows a search pill (desktop: full pill with placeholder + ⌘K hint;
 *     mobile: icon-only button). Clicking either opens the modal.
 *   • Modal opens with focused input + grouped results dropdown.
 *   • Cmd/Ctrl+K opens the modal from anywhere on the site.
 *   • Debounced fetch (200ms). Results grouped by category, max ~4 per group.
 *     "See all N results" link jumps to /search?q=… for overflow.
 *   • Keyboard: ↑/↓ to navigate rows, Enter to open, Esc to close.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, BookOpen, Newspaper, HourglassHigh, Storefront, Question, Tag, ForkKnife, BookBookmark } from "@phosphor-icons/react";
import api from "@/lib/api";

const GROUP_META = {
  recipes:     { label: "Recipes",              Icon: ForkKnife },
  cookbooks:   { label: "Cookbooks",            Icon: BookBookmark },
  news:        { label: "News",                 Icon: Newspaper },
  historical:  { label: "Historical recipes",   Icon: HourglassHigh },
  ingredients: { label: "Ingredients",          Icon: BookOpen },
  tags:        { label: "Tags",                 Icon: Tag },
  shop:        { label: "Shop",                 Icon: Storefront },
  faqs:        { label: "FAQs",                 Icon: Question },
};
const GROUP_ORDER = ["recipes", "cookbooks", "historical", "news", "ingredients", "tags", "shop", "faqs"];

// Highlight the matched substring inside a title/subtitle.
function Highlight({ text, q }) {
  if (!text || !q) return <>{text || ""}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-rf-amber/40 text-rf-ink rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ groups: {}, total: 0 });
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // Flatten visible rows for keyboard nav — same order as render.
  const flatRows = useMemo(() => {
    const rows = [];
    for (const key of GROUP_ORDER) {
      const items = data.groups?.[key] || [];
      for (const it of items) rows.push({ ...it, _group: key });
    }
    return rows;
  }, [data]);

  // Cmd/Ctrl+K opens; Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus input on open, reset state on close.
  useEffect(() => {
    if (open) {
      // Double-RAF ensures the input has actually mounted before we focus,
      // even in slow-render environments (testing agent flagged 50ms setTimeout
      // as occasionally too slow).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => inputRef.current?.focus());
      });
    } else {
      setQ("");
      setData({ groups: {}, total: 0 });
      setCursor(0);
    }
  }, [open]);

  // Debounced fetch — 200ms after last keystroke, only when q length >= 2.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setData({ groups: {}, total: 0 });
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(term)}&limit=4`, { signal: ctrl.signal });
        setData(r.data || { groups: {}, total: 0 });
        setCursor(0);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setData({ groups: {}, total: 0 });
        }
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [q, open]);

  const goTo = useCallback((url) => {
    setOpen(false);
    if (url.startsWith("http")) window.location.href = url;
    else navigate(url);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!flatRows.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flatRows.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === "Enter") {
      e.preventDefault();
      const row = flatRows[cursor];
      if (row?.url) goTo(row.url);
      else if (q.trim().length >= 2) goTo(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <>
      {/* Trigger — icon-only, always. Consistent across all viewports. */}
      <button
        type="button"
        data-testid="global-search-trigger"
        onClick={() => setOpen(true)}
        title="Search (⌘K)"
        aria-label="Search RecipeFiler"
        className="p-2 rounded-full text-rf-ink-soft hover:text-rf-orange hover:bg-rf-cream-alt transition-colors"
      >
        <MagnifyingGlass size={20} weight="bold" />
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-rf-ink/40 backdrop-blur-sm"
          data-testid="global-search-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="max-w-2xl mx-auto mt-16 md:mt-24 px-4">
            <div className="bg-rf-cream rounded-2xl shadow-2xl border border-rf-ink/10 overflow-hidden">
              {/* Input */}
              <div className="flex items-center gap-3 px-4 md:px-5 h-14 border-b border-rf-ink/10">
                <MagnifyingGlass size={20} weight="bold" className="text-rf-ink-soft shrink-0" />
                <input
                  ref={inputRef}
                  data-testid="global-search-input"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search recipes, cookbooks, ingredients, news, historical, shop…"
                  className="flex-1 bg-transparent outline-none text-rf-ink placeholder:text-rf-ink-soft/60 text-base"
                />
                {loading && <span className="text-xs text-rf-ink-soft animate-pulse">searching…</span>}
                <button onClick={() => setOpen(false)} className="text-rf-ink-soft hover:text-rf-ink" aria-label="Close search">
                  <X size={20} weight="bold" />
                </button>
              </div>

              {/* "Did you mean" chips — synonym expansion hint. Only
                  shown when the smart-match earned results (e.g. a
                  search for "aubergine" that pulled an "eggplant"
                  recipe will chip "Also matched: eggplant, brinjal").
                  Tapping a chip re-runs the search with that term as
                  the explicit query so the user can narrow down. */}
              {Array.isArray(data?.expansion?.extras) && data.expansion.extras.length > 0 && (data.total || 0) > 0 && (
                <div
                  data-testid="search-synonym-chips"
                  className="flex flex-wrap items-center gap-1.5 px-5 py-2 border-b border-rf-ink/5 bg-rf-cream-alt/50"
                >
                  <span className="text-[11px] uppercase tracking-wider font-bold text-rf-ink-soft shrink-0">
                    Also matched:
                  </span>
                  {data.expansion.extras.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`search-chip-${t}`}
                      onClick={() => setQ(t)}
                      className="text-xs font-bold px-2.5 py-1 rounded-full bg-white border border-rf-ink/15 text-rf-ink hover:bg-rf-amber/10 hover:border-rf-amber transition-colors"
                      title={`Search for "${t}" instead`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}

              {/* Results */}
              <div className="max-h-[65vh] overflow-y-auto" data-testid="global-search-results">
                {q.trim().length < 2 ? (
                  <div className="p-8 text-center text-sm text-rf-ink-soft">
                    Type at least 2 characters to search across recipes, cookbooks, news, ingredients, historical recipes, tags, shop &amp; FAQs.
                  </div>
                ) : (data.total || 0) === 0 && !loading ? (
                  <div className="p-8 text-center text-sm text-rf-ink-soft">
                    No results for <span className="font-bold text-rf-ink">&ldquo;{q.trim()}&rdquo;</span>.
                    <div className="mt-2 text-xs">Try broader terms like an ingredient, a tag, or a source book.</div>
                  </div>
                ) : (
                  <div className="py-2">
                    {GROUP_ORDER.map((key) => {
                      const items = data.groups?.[key] || [];
                      if (!items.length) return null;
                      const { label, Icon } = GROUP_META[key];
                      const RowIcon = Icon;
                      const globalStartIdx = flatRows.findIndex((r) => r._group === key);
                      return (
                        <div key={key} className="px-1 pt-2 pb-1" data-testid={`search-group-${key}`}>
                          <div className="flex items-center justify-between px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-rf-ink-soft">
                            <span className="flex items-center gap-1.5">
                              <Icon size={13} weight="bold" />
                              {label}
                            </span>
                            {items.length >= 4 && (
                              <button
                                onClick={() => goTo(`/search?q=${encodeURIComponent(q.trim())}&type=${key}`)}
                                className="text-[10px] normal-case tracking-normal text-rf-orange hover:underline font-semibold"
                              >
                                see all →
                              </button>
                            )}
                          </div>
                          {items.map((it, i) => {
                            const idx = globalStartIdx + i;
                            const active = cursor === idx;
                            return (
                              <button
                                key={`${key}-${i}`}
                                type="button"
                                onMouseEnter={() => setCursor(idx)}
                                onClick={() => goTo(it.url)}
                                data-testid={`search-result-${key}-${i}`}
                                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? "bg-rf-amber/20" : "hover:bg-rf-ink/5"}`}
                              >
                                {it.image_url ? (
                                  <img src={it.image_url} alt="" className="w-10 h-10 rounded-md object-cover shrink-0 border border-rf-ink/10" loading="lazy" />
                                ) : (
                                  <div className="w-10 h-10 rounded-md bg-rf-cream-alt border border-rf-ink/10 flex items-center justify-center shrink-0">
                                    <RowIcon size={16} weight="duotone" className="text-rf-ink-soft" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold text-rf-ink truncate">
                                    <Highlight text={it.title} q={q} />
                                  </div>
                                  {it.subtitle && (
                                    <div className="text-xs text-rf-ink-soft truncate">
                                      <Highlight text={it.subtitle} q={q} />
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                    {/* Footer see-all */}
                    <div className="border-t border-rf-ink/10 mt-2 px-4 py-3 flex items-center justify-between text-xs text-rf-ink-soft">
                      <span>Use ↑ ↓ to navigate · Enter to open · Esc to close</span>
                      <button
                        onClick={() => goTo(`/search?q=${encodeURIComponent(q.trim())}`)}
                        data-testid="search-see-all-link"
                        className="text-rf-orange hover:underline font-semibold"
                      >
                        See all {data.total} results →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
